import React from "react";
import { ShieldCheck, ScanLine } from "lucide-react";
import QrCode from "./QrCode";

/**
 * IdCardFront / IdCardBack — the two faces of the printed card.
 *
 * ⚠️ EVERY DIMENSION IS A RATIO OF THE CARD, NOT A PIXEL. The card is rendered
 * at three very different sizes — ~300px on screen, 53.98mm on paper, and 3×
 * that on a downloaded PNG — and one component draws all three. A photo sized
 * `tw-h-24` would be right at exactly one of them. So the face takes a `unit`
 * (a CSS length: `px`, `mm`) and every measurement is `calc(unit * n)`, which
 * makes the card resolution-independent by construction.
 *
 * The proportions are ID-1 / CR80, the bank-card standard every badge printer,
 * lanyard holder and wallet slot in the world is cut for: 53.98 × 85.60 mm.
 * Portrait, because that is what hangs on a lanyard.
 *
 * ⚠️ `print-color-adjust: exact` is REQUIRED on both faces. Browsers strip
 * background colours when printing by default, which would print the brand band
 * as white paper and the card as a wireframe of itself. It is written as an
 * arbitrary PROPERTY, which takes no `tw-` prefix inside the brackets — with one
 * it emits nothing, silently.
 *
 * ⚠️ Colours are tokens, so the card follows the institute's brand. The band is
 * `--primary-solid` with `--primary-solid-fg` as its ink, used AS A PAIR:
 * `--primary` is the ink token and is lifted in dark mode, where white fails on
 * it as a fill.
 */

/** ID-1 / CR80, portrait. Width is the unit; height is the ratio. */
export const CARD_ASPECT = 85.6 / 53.98;   // 1.5858

const COLOR_EXACT = "[print-color-adjust:exact] [-webkit-print-color-adjust:exact]";

/** `calc()` against the card's own width unit — see the header. */
const u = (unit, n) => `calc(${unit} * ${n})`;

/** The shared frame: exact aspect, clipped corners, brand-agnostic border. */
const Face = ({ unit, children, className = "" }) => (
  <div
    className={
      "tw-relative tw-flex tw-flex-col tw-overflow-hidden tw-border tw-border-solid " +
      "tw-border-line tw-bg-surface-raised tw-text-content " +
      COLOR_EXACT + " " + className
    }
    style={{
      width: unit,
      height: u(unit, CARD_ASPECT),
      // 3mm on a 54mm card — the radius a real card is punched with.
      borderRadius: u(unit, 0.055),
    }}
  >
    {children}
  </div>
);

/* ── Front ────────────────────────────────────────────────────────────────── */

export const IdCardFront = ({ card, unit = "300px" }) => {
  const { holder, institute, number } = card;
  const rows = (holder.rows || []).slice(0, 3);

  return (
    <Face unit={unit}>
      {/* Brand band. Its height is what leaves room for the photo to overlap. */}
      <div
        className={`tw-relative tw-flex tw-shrink-0 tw-items-center tw-gap-2 tw-bg-primary-solid tw-text-primary-solid-fg ${COLOR_EXACT}`}
        style={{ height: u(unit, 0.3), padding: `${u(unit, 0.05)} ${u(unit, 0.055)}` }}
      >
        {institute?.logoUrl && (
          <img
            src={institute.logoUrl}
            alt=""
            className="tw-shrink-0 tw-rounded-sm tw-bg-surface-raised tw-object-contain"
            style={{ width: u(unit, 0.115), height: u(unit, 0.115), padding: u(unit, 0.008) }}
          />
        )}
        <div className="tw-min-w-0 tw-flex-1">
          <div
            className="tw-truncate tw-font-semibold tw-leading-tight"
            style={{ fontSize: u(unit, 0.052) }}
          >
            {institute?.name || "Institute"}
          </div>
          {institute?.code && (
            <div
              className="tw-truncate tw-uppercase tw-tracking-caps tw-opacity-80"
              style={{ fontSize: u(unit, 0.032) }}
            >
              Code {institute.code}
            </div>
          )}
        </div>
      </div>

      {/* Photo, straddling the band edge — the shape every real badge uses. */}
      <div
        className="tw-relative tw-flex tw-shrink-0 tw-justify-center"
        style={{ marginTop: u(unit, -0.09) }}
      >
        <div
          className={`tw-overflow-hidden tw-rounded-full tw-border-solid tw-border-surface-raised tw-bg-surface-sunken ${COLOR_EXACT}`}
          style={{
            width: u(unit, 0.34),
            height: u(unit, 0.34),
            borderWidth: u(unit, 0.013),
          }}
        >
          {holder.photoUrl ? (
            <img
              src={holder.photoUrl}
              alt=""
              className="tw-h-full tw-w-full tw-object-cover"
            />
          ) : (
            <div
              className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-font-semibold tw-text-content-subtle"
              style={{ fontSize: u(unit, 0.13) }}
            >
              {(holder.name || "?").charAt(0).toUpperCase()}
            </div>
          )}
        </div>
      </div>

      {/* Identity */}
      <div
        className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-items-center"
        style={{ padding: `${u(unit, 0.035)} ${u(unit, 0.055)} 0` }}
      >
        <div
          className="tw-w-full tw-text-center tw-font-semibold tw-leading-tight tw-text-content"
          style={{ fontSize: u(unit, 0.062) }}
        >
          {holder.name}
        </div>
        <div
          className={`tw-mt-1 tw-rounded-full tw-bg-primary/10 tw-font-semibold tw-uppercase tw-tracking-caps tw-text-primary ${COLOR_EXACT}`}
          style={{
            fontSize: u(unit, 0.032),
            padding: `${u(unit, 0.008)} ${u(unit, 0.03)}`,
          }}
        >
          {holder.roleLabel}
        </div>

        {rows.length > 0 && (
          <dl
            className="tw-m-0 tw-mt-auto tw-w-full"
            style={{ marginTop: u(unit, 0.038) }}
          >
            {rows.map((r) => (
              <div
                key={r.label}
                className="tw-flex tw-items-baseline tw-justify-between tw-gap-2 tw-border-0 tw-border-b tw-border-dotted tw-border-line-soft"
                style={{ padding: `${u(unit, 0.012)} 0` }}
              >
                <dt
                  className="tw-shrink-0 tw-uppercase tw-tracking-caps tw-text-content-subtle"
                  style={{ fontSize: u(unit, 0.03) }}
                >
                  {r.label}
                </dt>
                <dd
                  className="tw-m-0 tw-min-w-0 tw-truncate tw-text-right tw-font-medium tw-text-content"
                  style={{ fontSize: u(unit, 0.036) }}
                >
                  {r.value}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </div>

      {/* Card number strip. Monospace, because this is the value a person reads
          aloud or types into a register — a proportional font makes an O and a
          0 the same glyph, which is the whole reason the alphabet excludes them
          server-side. */}
      <div
        className={`tw-flex tw-shrink-0 tw-items-center tw-justify-center tw-gap-1 tw-bg-surface-sunken tw-font-mono tw-tracking-wide tw-text-content-muted ${COLOR_EXACT}`}
        style={{ fontSize: u(unit, 0.038), padding: `${u(unit, 0.022)} ${u(unit, 0.04)}` }}
      >
        <ShieldCheck style={{ width: u(unit, 0.04), height: u(unit, 0.04) }} />
        {number}
      </div>
    </Face>
  );
};

/* ── Back ─────────────────────────────────────────────────────────────────── */

export const IdCardBack = ({ card, unit = "300px", formatDate }) => {
  const { institute, number, verify, validUntil, issuedAt } = card;
  const fmt = formatDate || ((d) => (d ? new Date(d).toLocaleDateString("en-IN", {
    year: "numeric", month: "short", timeZone: "Asia/Kolkata",
  }) : "—"));

  return (
    <Face unit={unit}>
      <div
        className={`tw-flex tw-shrink-0 tw-items-center tw-justify-center tw-bg-primary-solid tw-font-semibold tw-uppercase tw-tracking-caps tw-text-primary-solid-fg ${COLOR_EXACT}`}
        style={{ height: u(unit, 0.09), fontSize: u(unit, 0.036) }}
      >
        Identity Card
      </div>

      <div
        className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-items-center"
        style={{ padding: `${u(unit, 0.045)} ${u(unit, 0.055)}` }}
      >
        {/* 46% of the card width. On a 54mm card that is ~25mm, which with a
            41-module code is 0.6mm per module — above the ~0.5mm floor where
            phone cameras and cheap gate scanners start to fail. See
            utils/identityCard.js for why the token is packed binary. */}
        <QrCode
          size={verify?.qrSize}
          path={verify?.qrPath}
          className="tw-text-content"
          style={{ width: u(unit, 0.46), height: u(unit, 0.46) }}
          title={`Scan to verify card ${number}`}
        />
        <div
          className="tw-mt-1 tw-inline-flex tw-items-center tw-gap-1 tw-text-content-subtle"
          style={{ fontSize: u(unit, 0.03) }}
        >
          <ScanLine style={{ width: u(unit, 0.032), height: u(unit, 0.032) }} />
          Scan to verify
        </div>

        <div
          className="tw-mt-auto tw-w-full tw-text-center"
          style={{ marginTop: u(unit, 0.04) }}
        >
          <div
            className="tw-font-mono tw-tracking-wide tw-text-content"
            style={{ fontSize: u(unit, 0.038) }}
          >
            {number}
          </div>
          <div
            className="tw-mt-0.5 tw-text-content-subtle"
            style={{ fontSize: u(unit, 0.028) }}
          >
            Issued {fmt(issuedAt)} · Valid to {fmt(validUntil)}
          </div>
        </div>

        <div
          className="tw-w-full tw-border-0 tw-border-t tw-border-solid tw-border-line-soft tw-text-center"
          style={{ marginTop: u(unit, 0.035), paddingTop: u(unit, 0.03) }}
        >
          <p
            className="tw-m-0 tw-leading-snug tw-text-content-muted"
            style={{ fontSize: u(unit, 0.028) }}
          >
            This card remains the property of {institute?.name || "the institute"}.
            If found, please return to the address below.
          </p>
          {(institute?.address || institute?.phone || institute?.email) && (
            <p
              className="tw-m-0 tw-leading-snug tw-text-content-subtle"
              style={{ fontSize: u(unit, 0.027), marginTop: u(unit, 0.015) }}
            >
              {[institute.address, institute.phone, institute.email].filter(Boolean).join(" · ")}
            </p>
          )}
        </div>
      </div>
    </Face>
  );
};

export default IdCardFront;
